import React, { useEffect, useState } from "react";
import axios from "axios";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { trackPromise } from "react-promise-tracker";
import { MultiSelect } from "react-multi-select-component";

import usercreditsService from "../api/creditmanagementService";
import useCredit from "../api/userCredit";
import { User } from "../types";
import { roleTypes } from "../constants";
import LoadingOverlay from "../components/LoadingOverlay/LoadingOverlay";

import "./UserForm.scss";
import { ComboBox, IComboBoxOption } from "@fluentui/react";

const api = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL,
  headers: { "Content-Type": "application/json" },
});

interface UserFormProps {
  closeModal: () => void;
  modalHeader: string;
  userObj: User | null;
  fetchUsers: () => void;
  creditPoolBalance: number;
  useCreditPool: boolean;
  setCreditPoolBalance: () => void;
  allActiveCategories: any;
  mode: "add" | "update";
}

const UserForm: React.FC<UserFormProps> = ({
  userObj,
  modalHeader,
  closeModal,
  fetchUsers,
  useCreditPool,
  creditPoolBalance,
  setCreditPoolBalance,
  allAciveCategories,
  mode,
  
}) => {
  const [currentUser, setCurrentUser] = useState<User>(() => {
    if (mode === "update" && userObj) {
      return userObj;
    } else {
      return {
        email: "",
        username :"",
        role: "General", 
        CreditAssigned: 0,
        categories: [],
      };
    }
  });

  console.log("---------->")
  console.log(userObj);


  const [credits, setCredits] = useState<number>(0);
  const [error, setError] = useState(null);
  const [creditAction, setCreditAction] = useState("assign");
  const [enableUpdateUser, setEnableUpdateUser] = useState(false);
  const [submitButtonText, setSubmitButtonText] = useState(mode === "add" ? "Add" : "Update");
  const [loading, setLoading] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState(mode === "add" ? "Creating User..." : "Updating User...");
  const dataloaderMessage = mode === "add" ? "Preparing form..." : "Fetching user details...";
  const [allCategories, setAllCategories] = useState(allAciveCategories);
  const originalUserCategories = userObj?.categories
  const [allUserCategories, setAllUserCategories] = useState([]);
  const { creditBalance, setCreditBalance } = useCredit();
  const login_email = localStorage.getItem('email');

  const handleCancel = () => {
    closeModal();
  };

  const changeCreditAction = (event) => {
    setCreditAction(event.target.value);
  };


  // Validating credit assigning and revoking
  const validateCreditAction = () => {
    let enable = true;
    if (
      creditAction === "assign" &&
      useCreditPool &&
      credits > creditPoolBalance
    ) {
      setError(
        `Credits assigned exceeds credit pool balance ${creditPoolBalance}`
      );
      enable = false;
    }
    if (creditAction === "revoke" && credits > userObj?.balance) {
      setError("Not enough balance to revoke");
      enable = false;
    }
    return enable;
  };

  // validating update user form
  useEffect(() => {
    if (credits < 0) {
      setError("Credits cannot be less than 0");
      setEnableUpdateUser(false);
    } else if (
      allCategories.length > 0 &&
      JSON.stringify(allUserCategories) !==
      JSON.stringify(originalUserCategories)
    ) {
      setEnableUpdateUser(true);
    } else if (credits == 0 && userObj.role !== currentUser.role) {
      setEnableUpdateUser(true);
      setError("");
    } else if (credits == 0 && userObj.role === currentUser.role) {
      setEnableUpdateUser(false);
    } else if (credits > 0) {
      if (validateCreditAction()) {
        setEnableUpdateUser(true);
        setError(" ");
      } else {
        setEnableUpdateUser(false);
      }
    }
  }, [currentUser, credits, creditAction, allUserCategories]);

  useEffect(() => {
    let userCategories = allCategories.filter(category => userObj?.categories.includes(category.value));
    setAllUserCategories(userCategories);

  }, [userObj?.categories])

  const createUser = () => {
    setSubmitButtonText("Adding...");
    const categoriesPayload = allUserCategories.map(cat => cat.value)
    setLoading(true);
    const isCategoriesUpdated =
      allCategories.length > 0 &&
      JSON.stringify(allUserCategories) !==
      JSON.stringify(originalUserCategories);
    const options = {
      
      email: currentUser.email,
      new_role: currentUser.role,
      categories: categoriesPayload,
      name : currentUser.username
    };
  
    if (creditAction === "assign") {
      options["CreditAssigned"] = Number(credits);
    } else {
      options["Credit_Revoked"] = Number(credits);
    }

    options["admin_email"] = login_email;
    const user_info = JSON.stringify(options);
    
    trackPromise(
      usercreditsService
        .addUser(user_info)
        .then((response) => {
          const { message } = response?.data;
          setSubmitButtonText("Add");
          fetchUsers();
          alert(message || `User created successfully`);
          setLoading(false);
          closeModal();
        })
        .catch((err) => {
          const { error } = err.response.data;
          setSubmitButtonText("Add");
          setError(error);
          setLoading(false);
          alert(error);
        })
    );
  };


  const updateUser = () => {
    setSubmitButtonText("Updating...");
    const categoriesPayload = allUserCategories.map(cat => cat.value)
    setLoading(true);
    const isCategoriesUpdated =
      allCategories.length > 0 &&
      JSON.stringify(allUserCategories) !==
      JSON.stringify(originalUserCategories);
    const options = {
      email: currentUser.email, // Include email field
      id: currentUser.id,
      new_role: currentUser.role,
      categories: categoriesPayload,
    };

    if (creditAction === "assign") {
      options["CreditAssigned"] = Number(credits);
    } else {
      options["Credit_Revoked"] = Number(credits);
    }

    const user_info = JSON.stringify(options);
    trackPromise(
      usercreditsService
        .updateUsers(user_info)
        .then((response) => {
          const { email = "", balance, credit_pool_balance } = response?.data;
          setSubmitButtonText("Update");
          fetchUsers();
          if (email === localStorage.getItem("email")) {
            if (balance !== creditBalance) {
              setCreditBalance(balance);
            }
          }
          setCreditPoolBalance(credit_pool_balance);

          alert(
            response.data.message ||
            `User record updated successfully`
          );
          setLoading(false);
          closeModal();
        })
        .catch((err) => {
          const { error } = err.response.data;
          setSubmitButtonText("Update");
          setError(error);
          setLoading(false);
          alert(error);
        })
    );
  };

  const handleSubmit = () => {
    if (mode === "add") {
      createUser();
    } else {
      updateUser();
    }
  };

  const setUserRole = (e) => {
  
    setCurrentUser((prevUser) => ({
      ...prevUser,
      role: e.target.value,
    }));
  };

  return (
    <div>
      <div className="overlay">
        {loading && <LoadingOverlay message={loaderMessage} />}
        <div className="container h-100 d-flex justify-content-center align-items-center section-view frame">
          <div className="pop-card-pos p-4 bg-white rounded shadow-lg datasetoverlayelm uploadStyle">
            <div className="d-flex flex-row justify-content-space-between align-items-center">
              <div className="d-inline add-dataset-title">{modalHeader}</div>
            </div>
            <div className="col-auto pad-l-0 mt-2">
              <div className="card choosefile-card">
                <div className="col-12 mt-2">
                  <div className="d-flex flex-column justify-content-space-evenly">
                    {/* <div className="d-flex flex-row"> */}
                      {mode === "update" && (
                          <div className="d-flex flex-row margin-bt">
                            <label> Name: </label>{" "}
                            <p className="username">{currentUser?.username}</p>
                          </div>
                      )}
                      {mode === "add" && (
                        <div className="d-flex flex-row margin-bt">
                          <label>Email: </label>
                          <input
                            type="email"
                            className="form-control shadow-none"
                            value={currentUser?.email}
                            onChange={(e) =>
                              setCurrentUser((prevUser) => ({
                                ...prevUser,
                                email: e.target.value,
                              }))
                            }
                          />
                        </div>
                        
                      )}
                       {mode === "add" && (
                        <div className="d-flex flex-row margin-bt">
                          <label>Name: </label>
                          <input
                            type="text"
                            className="form-control shadow-none"
                            value={currentUser?.username}
                            onChange={(e) =>
                              setCurrentUser((prevUser) => ({
                                ...prevUser,
                                username: e.target.value,
                              }))
                            }
                          />
                        </div>
                        
                      )}
                      
                    
                    {/* </div> */}
                    <div className="d-flex flex-row margin-bt">
                      <label>Role: </label>
                      <select
                        className="form-control shadow-none"
                        name="role"
                        value={currentUser.role}
                        onChange={setUserRole}
                      >
                        <option disabled value="">
                          Select an option
                        </option>
                        {roleTypes.map((ele) => (
                          <option key={ele.name} value={ele.name}>
                            {ele.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                 
                  <div className="d-flex flex-row justify-content-space-evenly margin-bt">
                    <div>
                      <label>
                        <input
                          type="radio"
                          value="assign"
                          checked={creditAction === "assign"}
                          onChange={changeCreditAction}
                        />
                        Assign
                      </label>
                      <label>
                        <input
                          type="radio"
                          value="revoke"
                          checked={creditAction === "revoke"}
                          onChange={changeCreditAction}
                        />
                        Revoke
                      </label>
                    </div>
                    <br />
                    <div className="d-flex flex-row offset-md-1">
                      <label>Credits: </label>
                      <input
                        defaultValue={credits}
                        type="number"
                        name="credit_revoked"
                        className="form-control shadow-none"
                        onChange={(e) => setCredits(Number(e.target.value))}
                      />
                    </div>
                  </div>                  
                  <div className="d-flex flex-row justify-content-space-evenly" style={{ alignItems: 'center' }}>
                    <h6 className="category-css">Categories</h6>
                    <div className="col-6" style={{ marginLeft: "8px" }}>
                      <MultiSelect
                        options={allCategories}
                        value={allUserCategories || []}
                        onChange={(selected) => setAllUserCategories(selected)}
                        labelledBy="Select Domains"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="errorText">{error ? error : ""}</div>
              <Form>
                <div className="mt-2">
                  <div className="d-flex flex-row-reverse">
                    <Button
                      className="mt-2 cancel-btn btn-sm"
                      variant="primary"
                      onClick={handleCancel}
                    >
                      {" "}
                      Cancel{" "}
                    </Button>
                    <Button
                      className="mt-2 create-btn btn-sm"
                      variant="primary"
                      disabled={!enableUpdateUser}
                      onClick={handleSubmit}
                    >
                      {" "}
                      {submitButtonText}{" "}
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserForm;
